import { ArrowUpRight } from "lucide-react";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { AuroraBackground } from "@/components/aurora-background";
import { urlFor, type GeoResource } from "@/lib/sanity";

type Props = {
  items: GeoResource[];
};

export function GeoResourcesGallery({ items }: Props) {
  if (!items || items.length === 0) return null;

  return (
    <section className="relative px-6 py-24 overflow-hidden">
      <AuroraBackground className="opacity-30" />
      <div className="relative max-w-7xl mx-auto">
        <div className="max-w-3xl mb-10">
          <SectionEyebrow>GEO resources</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance">
            Go deeper on <span className="text-gradient">Generative Engine Optimization</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => {
            const href = item.externalUrl;
            const Wrapper: React.ElementType = href ? "a" : "div";
            const wrapperProps: Record<string, unknown> = href
              ? { href, target: "_blank", rel: "noopener noreferrer" }
              : {};
            const imgUrl = item.coverImage
              ? urlFor(item.coverImage).width(800).height(450).fit("crop").auto("format").url()
              : null;

            return (
              <Wrapper
                key={item._id}
                {...wrapperProps}
                className="group relative glass-card overflow-hidden hover-lift hover:-translate-y-1 hover:border-white/25 transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-purple-500/20 via-pink-500/10 to-transparent">
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={item.title}
                      loading="lazy"
                      className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div
                      aria-hidden
                      className="absolute inset-0"
                      style={{
                        background:
                          "radial-gradient(120% 80% at 20% 0%, rgba(217,70,239,0.35), transparent 60%), radial-gradient(120% 80% at 100% 100%, rgba(139,92,246,0.35), transparent 60%)",
                      }}
                    />
                  )}
                </div>

                <div className="relative p-6 flex flex-col gap-3 flex-1">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
                    {item.category ? (
                      <span className="text-pink-soft">{item.category}</span>
                    ) : null}
                    {item.category && item.readTime ? (
                      <span className="text-white/30">•</span>
                    ) : null}
                    {item.readTime ? (
                      <span className="text-white/60">{item.readTime}</span>
                    ) : null}
                  </div>

                  <h3 className="text-lg font-semibold text-white leading-snug text-balance group-hover:text-gradient transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-sm text-white/70 leading-relaxed line-clamp-3">
                    {item.excerpt}
                  </p>

                  {href ? (
                    <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-sm text-pink-soft group-hover:text-white transition-colors">
                      Read article
                      <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  ) : null}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
}
